import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import QuoteForm from "@/components/QuoteForm";
import { seoKeywords, keywordLinks } from "@/lib/seoKeywords";

const rates = [
  { state: "Connecticut", current: "15.62¢", previous: "15.11¢" },
  { state: "Delaware", current: "12.34¢", previous: "12.10¢" },
  { state: "Illinois", current: "11.28¢", previous: "10.96¢" },
  { state: "Maine", current: "16.44¢", previous: "16.02¢" },
  { state: "Maryland", current: "13.72¢", previous: "13.40¢" },
  { state: "Massachusetts", current: "21.15¢", previous: "20.60¢" },
  { state: "New Hampshire", current: "18.26¢", previous: "17.88¢" },
  { state: "New Jersey", current: "14.63¢", previous: "14.20¢" },
  { state: "New York", current: "22.58¢", previous: "21.91¢" },
  { state: "Ohio", current: "10.74¢", previous: "10.33¢" },
  { state: "Pennsylvania", current: "11.96¢", previous: "11.51¢" },
  { state: "Rhode Island", current: "19.42¢", previous: "18.97¢" },
  { state: "Texas", current: "9.84¢", previous: "9.53¢" },
  { state: "Washington D.C.", current: "15.07¢", previous: "14.72¢" },
];

export default function HomePage() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Commercial Electricity Brokerage",
      name: "Business Electricity Rate Comparison",
      description:
        "Compare commercial electricity rates and lower your business energy costs with a trusted commercial electricity provider and energy broker.",
      areaServed: "United States",
      keywords: seoKeywords,
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="container py-12 md:py-20 space-y-16">
      {/* Hero + Quote Form */}
      <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
        <div className="lg:w-2/3 space-y-8">
          <section>
            <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight text-foreground mb-4">
              Lower Your Business Electricity Cost With Fixed-Rate Commercial Power
            </h1>
            <p className="text-base md:text-lg font-body text-muted-foreground max-w-xl mb-6">
              Get a custom quote in 24 hours. Upload a bill or share usage.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button variant="action" size="lg" asChild>
                <Link to="/quote">Get a Quote</Link>
              </Button>
              <Button variant="outline" size="lg" asChild className="font-display font-semibold uppercase text-xs tracking-wide">
                <Link to="/upload">Upload Your Bill</Link>
              </Button>
            </div>
          </section>
        </div>
        <div className="lg:w-1/3">
          <div className="lg:sticky lg:top-20">
            <QuoteForm />
          </div>
        </div>
      </div>

      {/* Business Electricity Rates Table */}
      <section>
        <h2 className="font-display font-bold text-2xl md:text-3xl text-foreground text-center mb-2">
          Business Electricity Rates by Deregulated State
        </h2>
        <p className="text-sm md:text-base font-body text-muted-foreground text-center max-w-2xl mx-auto mb-8">
          Businesses in the following states can choose their electricity provider and compare commercial power rates.
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>State</TableHead>
              <TableHead>Current Month</TableHead>
              <TableHead>Previous Month</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rates.map((r, index) => (
              <TableRow key={r.state} className={index === 0 ? "bg-muted" : ""}>
                <TableCell className="font-semibold flex items-center gap-2">
                  {r.state}
                  <Badge className="bg-green-100 text-green-700 text-xs border-transparent hover:bg-green-200">
                    Choice Available
                  </Badge>
                </TableCell>
                <TableCell>{r.current}</TableCell>
                <TableCell>{r.previous}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      {/* Upload CTA */}
      <section className="border-t border-border pt-8 text-center space-y-4">
        <h2 className="font-display font-semibold text-xl text-foreground">Upload Your Electricity Bill</h2>
        <p className="text-sm font-body text-muted-foreground max-w-xl mx-auto">
          No account required. Upload a recent bill and receive a custom quote within 24 hours.
        </p>
        <Button variant="action" size="lg" asChild>
          <Link to="/upload">Upload Now</Link>
        </Button>
      </section>

      {/* Industries We Serve */}
      <section className="border-t border-border pt-8 text-center space-y-4">
        <h2 className="font-display font-semibold text-xl text-foreground">Industries We Serve</h2>
        <p className="text-sm font-body text-muted-foreground max-w-xl mx-auto">
          From manufacturing to retail, we help businesses across industries lower their electricity costs.
        </p>
        <Button variant="outline" size="lg" asChild>
          <Link to="/industries">View Industries</Link>
        </Button>
      </section>

      {/* How It Works */}
      <section className="border-t border-border pt-8 space-y-4">
        <h2 className="font-display font-semibold text-xl text-foreground text-center">How It Works</h2>
        <ol className="space-y-3 list-decimal list-inside text-sm font-body text-muted-foreground max-w-xl mx-auto">
          <li>Submit your usage details or upload a recent electricity bill.</li>
          <li>We compare rates from multiple commercial electricity suppliers.</li>
          <li>Receive a custom fixed-rate quote within 24 hours.</li>
          <li>Choose the best option. No equipment changes required.</li>
        </ol>
      </section>

      {/* Commercial Electricity Solutions */}
      <section className="border-t border-border pt-8 space-y-4">
        <h2 className="font-display font-semibold text-xl text-foreground text-center">
          Commercial Electricity Solutions for Businesses
        </h2>
        <p className="text-sm font-body text-muted-foreground max-w-xl mx-auto text-center">
          Our platform helps companies compare commercial electricity rates and connect with a
          trusted business electricity provider. Whether you need a competitive commercial power
          rate or a commercial energy broker to negotiate better contracts, we help businesses find
          the best business electricity rates available in their market.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {seoKeywords.map((keyword) => (
            <Link key={keyword} to={keywordLinks[keyword] || "/rates"}>
              <Badge variant="secondary" className="cursor-pointer hover:bg-secondary/70 transition-colors">
                {keyword}
              </Badge>
            </Link>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="border-t border-border pt-8 text-center space-y-4">
        <h2 className="font-display font-semibold text-xl text-foreground">Have Questions?</h2>
        <p className="text-sm font-body text-muted-foreground max-w-xl mx-auto">
          Reach out and we'll help you find the best commercial electricity rate for your business.
        </p>
        <Button variant="outline" size="lg" asChild>
          <Link to="/contact">Contact Us</Link>
        </Button>
      </section>
    </div>
  );
}
